import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../hooks/useLanguage'
import { useAuth } from '../hooks/useAuth'
import ResponsiveSidebar from '../components/layout/ResponsiveSidebar'
import { 
  UserIcon,
  LocationIcon,
  PhoneIcon,
  EmailIcon,
  MenuIcon,
  CloseIcon,
  BarChartIcon,
  FileIcon
} from '../components/icons/Icons'

const CitizenDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const { user, signOut } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const stats = [
    { title: 'Total Issues', count: 12, color: 'text-blue-600', bg: 'bg-blue-100' },
    { title: 'Pending', count: 5, color: 'text-yellow-600', bg: 'bg-yellow-100' },
    { title: 'In Progress', count: 4, color: 'text-orange-600', bg: 'bg-orange-100' },
    { title: 'Resolved', count: 3, color: 'text-green-600', bg: 'bg-green-100' }
  ]

  const recentIssues = [
    {
      id: 'CIV-001',
      title: 'Pothole on Main Street',
      category: 'Roads',
      status: 'In Progress',
      date: '2024-01-15',
      priority: 'High'
    },
    {
      id: 'CIV-002',
      title: 'Street Light Not Working',
      category: 'Electricity',
      status: 'Pending',
      date: '2024-01-14',
      priority: 'Medium'
    },
    {
      id: 'CIV-003',
      title: 'Garbage Collection Missed',
      category: 'Sanitation',
      status: 'Resolved',
      date: '2024-01-13',
      priority: 'Low'
    }
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Resolved': return 'text-green-600 bg-green-100'
      case 'In Progress': return 'text-orange-600 bg-orange-100'
      case 'Pending': return 'text-yellow-600 bg-yellow-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'text-red-600 bg-red-100'
      case 'Medium': return 'text-yellow-600 bg-yellow-100'
      case 'Low': return 'text-green-600 bg-green-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const handleSignOut = async () => {
    const { error } = await signOut()
    if (!error) {
      navigate('/login')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
              >
                {sidebarOpen ? <CloseIcon className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
              </button>
              <div className="flex items-center space-x-3 min-w-0">
                <img src="/logo.png" alt="Logo" className="w-8 h-8 flex-shrink-0" />
                <h1 className="text-xl font-semibold text-gray-900 truncate">{t('auth.citizenDashboard')}</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900">{user?.full_name || user?.email}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <button
                onClick={handleSignOut}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('auth.logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        <ResponsiveSidebar 
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          userRole="citizen"
          currentPage="dashboard"
        />

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6">
          <div className="max-w-7xl mx-auto space-y-5">
            {/* Welcome Section */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Welcome back, {user?.full_name || 'User'}!</h2>
              <p className="text-gray-600 text-sm">Track your civic issues and help make your community better.</p>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 gap-4">
              <button
                onClick={() => navigate('/report-issue')}
                className="bg-blue-600 text-white p-5 rounded-lg hover:bg-blue-700 transition-colors text-left"
              >
                <FileIcon className="w-8 h-8 mb-3" />
                <h3 className="text-lg font-semibold mb-2">Report New Issue</h3>
                <p className="text-blue-100 text-sm">Report a new civic issue in your area</p>
              </button>
              
              <button
                onClick={() => navigate('/my-issues')}
                className="bg-green-600 text-white p-5 rounded-lg hover:bg-green-700 transition-colors text-left"
              >
                <BarChartIcon className="w-8 h-8 mb-3" />
                <h3 className="text-lg font-semibold mb-2">Track Issues</h3>
                <p className="text-green-100 text-sm">View status of your reported issues</p>
              </button>
              
              <button
                onClick={() => navigate('/browse')}
                className="bg-purple-600 text-white p-5 rounded-lg hover:bg-purple-700 transition-colors text-left"
              >
                <LocationIcon className="w-8 h-8 mb-3" />
                <h3 className="text-lg font-semibold mb-2">Browse Area Issues</h3>
                <p className="text-purple-100 text-sm">See issues reported in your locality</p>
              </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center">
                    <div className={`${stat.bg} p-3 rounded-lg`}>
                      <div className={`w-6 h-6 ${stat.color}`}>
                        <BarChartIcon className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                      <p className="text-2xl font-bold text-gray-900">{stat.count}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Issues */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Recent Issues</h3>
                <button 
                  onClick={() => navigate('/my-issues')}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  View All
                </button>
              </div>
              
              {/* Mobile view for recent issues (default view) */}
              <div className="space-y-4">
                {recentIssues.map((issue) => (
                  <div 
                    key={issue.id} 
                    className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm"
                    onClick={() => navigate('/my-issues')}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-semibold text-gray-900">{issue.title}</h3>
                      <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-1">#{issue.id}</span>
                    </div>
                    <div className="flex items-center mb-3">
                      <span className="text-sm text-gray-600">{issue.category}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(issue.status)}`}>
                        {issue.status}
                      </span>
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getPriorityColor(issue.priority)}`}>
                        {issue.priority}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {issue.date}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop view for recent issues (hidden on mobile) */}
              <div className="hidden sm:block overflow-x-auto mt-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {recentIssues.map((issue) => (
                      <tr key={issue.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate('/my-issues')}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{issue.title}</div>
                            <div className="text-xs text-gray-500">{issue.id}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{issue.category}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusColor(issue.status)}`}>
                            {issue.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getPriorityColor(issue.priority)}`}>
                            {issue.priority}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{issue.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Emergency Contacts</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <PhoneIcon className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Emergency</p>
                    <p className="text-sm text-gray-600">100</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <PhoneIcon className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Municipal Helpline</p>
                    <p className="text-sm text-gray-600">1916</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <EmailIcon className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Support Email</p>
                    <p className="text-sm text-gray-600">support@civic.gov.in</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default CitizenDashboard